// Client-side router — sole owner of history.pushState and window.location reads.
// Components call navigate(path) and listen for app:navigate events.
//
// Phase 1 adds the auth surface (/login, /register, /reset, /verify) and the
// route guards. Later phases add /tickets, /projects, /kb, /reports, /admin
// (functional-spec §12.3).

import { store } from './store.js'
import { rememberReturnTo, takeReturnTo } from './return-to.js'

const ROUTES = [
  { pattern: /^\/login$/, component: 'page-login', auth: false },
  { pattern: /^\/register$/, component: 'page-register', auth: false },
  { pattern: /^\/reset$/, component: 'page-reset', auth: false },
  { pattern: /^\/verify$/, component: 'page-verify', auth: false },
  { pattern: /^\/csat$/, component: 'page-csat', auth: false },
  { pattern: /^\/$/, component: 'page-tickets', auth: true },
  { pattern: /^\/tickets$/, component: 'page-tickets', auth: true },
  { pattern: /^\/triage$/, component: 'page-triage', auth: true, agent: true },
  { pattern: /^\/clients$/, component: 'page-clients', auth: true, agent: true },
  { pattern: /^\/clients\/([0-9a-fA-F-]{36})$/, component: 'page-client', auth: true, agent: true },
  { pattern: /^\/groups$/, component: 'page-groups', auth: true, agent: true },
  { pattern: /^\/agents$/, component: 'page-agents', auth: true, agent: true },
  { pattern: /^\/reports$/, component: 'page-reports', auth: true, agent: true },
  { pattern: /^\/subscriptions$/, component: 'page-subscriptions', auth: true, agent: true },
  { pattern: /^\/connections$/, component: 'page-connections', auth: true, agent: true },
  { pattern: /^\/api-keys$/, component: 'page-api-keys', auth: true, agent: true },
  { pattern: /^\/oauth\/authorize$/, component: 'page-authorize', auth: true, agent: true },
  { pattern: /^\/kb$/, component: 'page-kb', auth: true },
  { pattern: /^\/kb\/articles\/([0-9a-fA-F-]{36})$/, component: 'page-kb-article', auth: true },
  { pattern: /^\/tickets\/(\d+)$/, component: 'page-ticket', auth: true }
]

// Public auth pages a signed-in user has no reason to sit on.
const REDIRECT_WHEN_AUTHED = new Set(['page-login', 'page-register'])

function match(pathname) {
  for (const route of ROUTES) {
    const m = pathname.match(route.pattern)
    if (m) return { ...route, params: m.slice(1) }
  }
  return { component: 'page-not-found', auth: false, params: [] }
}

export function navigate(path) {
  history.pushState(null, '', path)
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path } }))
}

// Re-render the current route without changing history (e.g. after auth loads).
export function rerender() {
  window.dispatchEvent(new CustomEvent('app:navigate'))
}

export function getRoute() {
  return match(window.location.pathname)
}

export function setupRouter(routerEl) {
  const render = () => {
    const state = store.get()
    if (!state.authReady) {
      routerEl.currentRoute = { component: 'app-loading', auth: false, params: [] }
      routerEl.requestUpdate?.()
      return
    }

    const route = match(window.location.pathname)

    // Unauthenticated users hitting a protected route go to login.
    if (route.auth && !state.user) {
      if (window.location.pathname !== '/login') {
        rememberReturnTo(window.location.pathname + window.location.search)
        navigate('/login')
      }
      routerEl.currentRoute = match('/login')
      routerEl.requestUpdate?.()
      return
    }

    // Agent-only routes are off-limits to clients — send them home.
    if (route.agent && state.user && state.user.role !== 'agent') {
      navigate('/')
      return
    }

    // Signed-in users don't need the login/register pages — they go on to whatever
    // they were interrupted doing, or home.
    if (REDIRECT_WHEN_AUTHED.has(route.component) && state.user) {
      navigate(takeReturnTo() || '/')
      return
    }

    routerEl.currentRoute = route
    routerEl.requestUpdate?.()
  }

  window.addEventListener('popstate', render)
  window.addEventListener('app:navigate', render)
  const unsubscribe = store.subscribe(render)

  render()

  return () => {
    window.removeEventListener('popstate', render)
    window.removeEventListener('app:navigate', render)
    unsubscribe()
  }
}
